import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import CodeBreaks from '../shared/code_breaks';
import { FilterBar, SelectFilter, Segmented } from '../shared/controls';
import { summary_line } from '../payouts/summary_line';
import { Ledger, Note, StatusCell, What, Who } from '../shared/cells';
import {
  fetch_rule_approvals,
  fetch_rule_changes,
  fetch_rules_catalogue,
  fetch_rules_list
} from './api';
import {
  FALLBACK_CATALOGUE,
  RULES_FILTERS_DEFAULT,
  RULE_STATUS_SEGMENTS,
  approval_row,
  approvals_missing,
  can_change_version,
  catalogue_view,
  change_from_search,
  change_log_mode,
  change_rules_text,
  change_status_view,
  day_label,
  filter_stages,
  list_view,
  merge_changes,
  prepare_again_of,
  queue_view,
  refusal,
  shown_rule_count,
  rules_filter_summary,
  rules_filters_on,
  stamp_ist,
  version_meta_text,
  version_status_view
} from './logic';
import RuleHistoryDialog from './history_dialog';
import RuleEditDialog from './edit_dialog';
import ChangePanel from './change_panel';
import { RULES_URL_KEYS, applicable_day, parse_rules_filters, rules_query } from './url_filters';
import { useInitialQuery, useQueryMirror } from '../shared/use_query_mirror';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import {
  RULE_CSV_COLUMNS,
  SCHEDULED_CSV_COLUMNS,
  approval_csv_columns,
  change_csv_columns,
  changes_csv_loader,
  rule_csv_rows,
  rules_csv_name
} from './csv';
import './rules_engine.css';

// Rule name (13/600), then its condition, muted, codes breaking only after an underscore.
const RuleCell = ({ r }) => (
  <Who
    name={r.name}
    meta={[
      r.sentence ? (
        <span className="tp-rx-sentence">
          <CodeBreaks text={r.sentence} />
        </span>
      ) : null
    ]}
  />
);

// The changes waiting for approval or approved and not saved yet, oldest asked first. Every payments user
// sees them; "Review" is offered only to someone other than the change's maker (the server decides).
const ApprovalsQueue = ({ queue, names, on_open, on_retry }) => {
  if (queue.status === 'missing') return null;
  const q = queue_view({ changes: queue.rows, truncated: queue.truncated });
  const rows = q.rows.map((x) => approval_row(x, names));
  const open_button = (r) => (
    <button
      type="button"
      className="tp-link"
      aria-label={`${r.action}: ${r.rules || 'rule change'}, asked ${r.asked_at}`}
      onClick={() => on_open(r.change_uuid)}
    >
      {r.action}
    </button>
  );
  const chip = (r) => <StatusPill tone={r.status.tone}>{r.status.label}</StatusPill>;
  const meta = (r) => [r.blocker ? <StatusPill tone="red">{r.blocker}</StatusPill> : null];
  // Four tracks (list-row pattern): asked · rules and reasons · money moved · status and Open.
  const columns = [
    {
      key: 'asked_at',
      role: 'identifier',
      label: 'Asked',
      track: { compact: '24%', full: '22%' },
      render: (r) => (
        <Who
          name={r.asked_at}
          meta={[
            <>
              By <span>{r.asked_by}</span>
            </>
          ]}
        />
      )
    },
    {
      key: 'rules',
      label: 'Rules',
      render: (r) => (
        <What title={r.rules} lines={[`Takes effect ${r.effective}`]}>
          {r.reasons.length ? (
            <ul className="tp-rx-reasons" aria-label="Needs approval because">
              {r.reasons.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          ) : null}
        </What>
      )
    },
    {
      key: 'moved',
      role: 'amount',
      label: 'Impact',
      align: 'right',
      track: { compact: '124px', full: '140px' },
      render: (r) => (
        <Ledger
          items={[{ label: 'Teachers', value: r.teachers }]}
          total={{ label: 'Rs moved', value: r.moved }}
        />
      )
    },
    {
      key: 'status',
      role: 'status',
      label: 'Status',
      track: { compact: '152px', full: '176px' },
      render: (r) => (
        <StatusCell
          chip={chip(r)}
          meta={meta(r)}
          actions={<span className="tp-c-acts tp-actions">{open_button(r)}</span>}
        />
      ),
      card_render: (r) => <StatusCell chip={chip(r)} meta={meta(r)} />
    },
    { key: 'open', role: 'actions', label: 'Open', in_table: false, card_render: open_button }
  ];
  return (
    <section className="tp-card" aria-labelledby="tp-rx-queue">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" id="tp-rx-queue">
          Waiting for approval{queue.status === 'ready' ? ` (${q.waiting})` : ''}
        </span>
        <span className="tp-card-hd-sub">
          Changes that need a second person. You cannot approve a change you prepared or sent.
        </span>
        <span className="tp-card-hd-acts">
          <DownloadCsvButton
            columns={approval_csv_columns(names)}
            rows={q.rows}
            file_name={() => rules_csv_name('waiting-for-approval')}
            disabled={queue.status !== 'ready'}
          />
        </span>
      </div>
      {queue.status === 'loading' && !queue.rows.length ? (
        <div className="tp-empty" role="status">
          Loading changes waiting for approval…
        </div>
      ) : null}
      {queue.status === 'error' ? <LoadFailed text={queue.error} on_retry={on_retry} /> : null}
      {queue.status !== 'error' && (queue.status !== 'loading' || queue.rows.length) ? (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.key}
          emptyText="No change is waiting for approval."
          card_below={660}
        />
      ) : null}
      {q.truncated_text ? <div className="tp-hint">{q.truncated_text}</div> : null}
    </section>
  );
};

const ChangesLog = ({
  log,
  names,
  on_open,
  on_retry,
  on_more,
  more_loading,
  more_error,
  load_all,
  on_error
}) => {
  const mode = change_log_mode(log.status, log.rows.length);
  const open_button = (c) => {
    let label = 'View';
    if (c.status === 'PREVIEWED') label = 'Preview and save';
    else if (c.status === 'APPROVED') label = 'Save';
    else if (c.status === 'PENDING_APPROVAL') label = 'Review';
    return (
      <button
        type="button"
        className="tp-link"
        aria-label={`${label}: ${change_rules_text(c, names) || 'rule change'}, prepared ${stamp_ist(c.created_at_ist)}`}
        onClick={() => on_open(c.change_uuid)}
      >
        {label}
      </button>
    );
  };
  const chip = (c) => {
    const st = change_status_view(c.status);
    return <StatusPill tone={st.tone}>{st.label}</StatusPill>;
  };
  // Three tracks: prepared · rules · status and Open; the reason runs under the row.
  const columns = [
    {
      key: 'created',
      role: 'identifier',
      label: 'Prepared',
      track: { compact: '24%', full: '22%' },
      render: (c) => (
        <Who
          name={stamp_ist(c.created_at_ist)}
          meta={[
            <>
              By <span>{(c.requested_by && c.requested_by.name) || '—'}</span>
            </>
          ]}
        />
      )
    },
    {
      key: 'rules',
      label: 'Rules',
      render: (c) => (
        <What
          title={change_rules_text(c, names)}
          lines={[`Takes effect ${day_label(c.effective_date)}`]}
        />
      )
    },
    {
      key: 'status',
      role: 'status',
      label: 'Status',
      track: { compact: '160px', full: '184px' },
      render: (c) => (
        <StatusCell
          chip={chip(c)}
          actions={<span className="tp-c-acts tp-actions">{open_button(c)}</span>}
        />
      ),
      card_render: (c) => <StatusCell chip={chip(c)} />
    },
    {
      key: 'note',
      label: 'Why',
      line: { from: 'rules', bare: true },
      render: (c) =>
        c.note ? <Note label="Why" text={<span className="tp-rx-why">{c.note}</span>} /> : null
    },
    { key: 'open', role: 'actions', label: 'Open', in_table: false, card_render: open_button }
  ];
  return (
    <section className="tp-card" aria-labelledby="tp-rx-log">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" id="tp-rx-log">
          Rule changes
        </span>
        <span className="tp-card-hd-sub">
          Newest first. A change not saved yet can be previewed again, sent for approval and saved
          here.
        </span>
        <span className="tp-card-hd-acts">
          <DownloadCsvButton
            columns={change_csv_columns(names)}
            load_rows={load_all}
            file_name={() => rules_csv_name('rule-changes')}
            disabled={mode !== 'table'}
            on_error={on_error}
          />
        </span>
      </div>
      {mode === 'loading' ? (
        <div className="tp-empty" role="status">
          Loading rule changes…
        </div>
      ) : null}
      {mode === 'error' ? <LoadFailed text={log.error} on_retry={on_retry} /> : null}
      {mode === 'table' ? (
        <DataTable
          columns={columns}
          rows={log.rows}
          rowKey={(c) => c.change_uuid}
          emptyText="No rule changes yet."
          card_below={660}
        />
      ) : null}
      {mode === 'table' && more_error ? (
        <div className="tp-err" role="alert">
          {more_error}
        </div>
      ) : null}
      {mode === 'table' && log.status === 'ready' && log.next_before ? (
        <div className="tp-rx-more">
          <button type="button" className="tp-btn-out" disabled={more_loading} onClick={on_more}>
            {more_loading ? 'Loading…' : 'Show older changes'}
          </button>
        </div>
      ) : null}
    </section>
  );
};

const RulesEngineTab = ({
  on_error,
  now = () => new Date(),
  poll_ms = 3000,
  queue_poll_ms = 60000,
  new_request_id,
  on_approvals_changed,
  on_approvals_count
}) => {
  const url = useInitialQuery(parse_rules_filters);
  const [at, setAt] = useState(url.at);
  // The date box keeps what is typed; only a whole date (or empty) is applied and fetched.
  const [at_draft, setAtDraft] = useState(url.at);
  useEffect(() => setAtDraft(at), [at]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [list_data, setListData] = useState(null);
  const [catalogue, setCatalogue] = useState(FALLBACK_CATALOGUE);
  const [error, setError] = useState('');
  const [rev, setRev] = useState(0);
  const [filters, setFilters] = useState(url.filters);
  useQueryMirror(RULES_URL_KEYS, rules_query(filters, at));
  const [log, setLog] = useState({ status: 'loading', rows: [], next_before: null, error: '' });
  const [more_loading, setMoreLoading] = useState(false);
  const [more_error, setMoreError] = useState('');
  // Bumped per change-log read: only the latest answer lands, so a page-1 reload beats an older page.
  const log_seq = useRef(0);
  const more_busy = useRef(false);
  const [history_key, setHistoryKey] = useState('');
  const [editing, setEditing] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [open_change, setOpenChange] = useState(() => change_from_search(window.location.search));
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState({ status: 'loading', rows: [], truncated: false, error: '' });
  const queue_seq = useRef(0);
  // A ref, so a new callback identity does not restart the queue's reads.
  const count_to = useRef(on_approvals_count);
  count_to.current = on_approvals_count;

  const reload = useCallback(() => setRev((r) => r + 1), []);

  useEffect(() => {
    let cancelled = false;
    fetch_rules_catalogue()
      .then((data) => {
        if (!cancelled) setCatalogue(catalogue_view(data));
      })
      .catch(() => {
        // Labels only: the fallbacks still read, and the server checks every value on save.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError('');
    fetch_rules_list(at)
      .then((data) => {
        if (cancelled) return;
        setListData(data);
        setStatus('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        const r = refusal(e, 'Could not load the rules.');
        if (r.permission) on_error(e, r.text);
        setError(r.text);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [at, rev, on_error]);

  const load_changes = useCallback(
    (before) => {
      if (before && more_busy.current) return;
      log_seq.current += 1;
      const s = log_seq.current;
      more_busy.current = !!before;
      setMoreLoading(!!before);
      setMoreError('');
      if (!before) setLog((l) => ({ ...l, status: 'loading', error: '' }));
      fetch_rule_changes(before || 0)
        .then((data) => {
          if (s !== log_seq.current) return;
          setLog((l) => ({
            status: 'ready',
            rows: merge_changes(l.rows, data && data.changes, !!before),
            next_before: (data && data.next_before_id) || null,
            error: ''
          }));
        })
        .catch((e) => {
          if (s !== log_seq.current) return;
          const r = refusal(e, 'Could not load the rule changes.');
          if (r.permission) on_error(e, r.text);
          if (before) setMoreError(r.text);
          else setLog((l) => ({ ...l, status: 'error', error: r.text }));
        })
        .finally(() => {
          if (s !== log_seq.current) return;
          more_busy.current = false;
          setMoreLoading(false);
        });
    },
    [on_error]
  );
  useEffect(() => {
    load_changes(0);
  }, [rev, load_changes]);

  // quiet: a background refresh keeps the rows on screen, and a failed one keeps the last good list.
  const load_approvals = useCallback(
    (quiet = false) => {
      queue_seq.current += 1;
      const s = queue_seq.current;
      if (!quiet) setQueue((q) => ({ ...q, status: 'loading', error: '' }));
      fetch_rule_approvals()
        .then((data) => {
          if (s !== queue_seq.current) return;
          const v = queue_view(data);
          setQueue({ status: 'ready', rows: v.rows, truncated: v.truncated, error: '' });
          if (count_to.current) count_to.current(v.waiting);
        })
        .catch((e) => {
          if (s !== queue_seq.current) return;
          if (approvals_missing(e)) {
            setQueue({ status: 'missing', rows: [], truncated: false, error: '' });
            if (count_to.current) count_to.current(0);
            return;
          }
          const r = refusal(e, 'Could not load the changes waiting for approval.');
          if (r.permission) on_error(e, r.text);
          if (!quiet) setQueue((q) => ({ ...q, status: 'error', error: r.text }));
        });
    },
    [on_error]
  );
  useEffect(() => {
    load_approvals();
  }, [rev, load_approvals]);
  // Others approve and ask meanwhile: re-read the queue while this page is on screen.
  useEffect(() => {
    if (!queue_poll_ms) return undefined;
    const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
    const t = setInterval(() => {
      if (visible()) load_approvals(true);
    }, queue_poll_ms);
    const on_visible = () => {
      if (visible()) load_approvals(true);
    };
    document.addEventListener('visibilitychange', on_visible);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', on_visible);
    };
  }, [queue_poll_ms, load_approvals]);
  // Any write in an open change (ask, approve, reject, withdraw, save) moves it in the queue and the log.
  const changed = useCallback(() => {
    load_approvals();
    load_changes(0);
    if (on_approvals_changed) on_approvals_changed();
  }, [load_approvals, load_changes, on_approvals_changed]);
  // A late answer after the tab unmounts is dropped.
  useEffect(
    () => () => {
      log_seq.current += 1;
      queue_seq.current += 1;
    },
    []
  );

  const view = useMemo(() => list_view(list_data || {}, catalogue), [list_data, catalogue]);
  const stages = useMemo(() => filter_stages(view.stages, filters), [view.stages, filters]);
  const viewing_today = !at || at === view.today;
  const stage_label = (code) => (catalogue.stages.find((st) => st.code === code) || {}).label;
  const clear_filters = () => {
    setFilters(RULES_FILTERS_DEFAULT);
    setAt('');
  };
  // The pop-up names the rules it changes once the log or the queue has the change.
  const change_title = (uuid) => {
    const c = log.rows.concat(queue.rows).find((x) => x && x.change_uuid === uuid);
    const rules = c ? change_rules_text(c, view.names) : '';
    return rules ? `Rule change: ${rules}` : 'Rule change';
  };
  let edit_block = '';
  if (!view.can_change)
    edit_block = 'No date is open for a change: every allowed date falls in a closed month.';
  else if (!viewing_today)
    edit_block = 'Changes start from the rules in force today. Clear the date to edit.';

  const version_chip = (r) => {
    const st = version_status_view(r.status);
    return <StatusPill tone={st.tone}>{st.label}</StatusPill>;
  };
  const version_meta = (r) => [version_meta_text(r)];
  // Three tracks (list-row pattern): rule and condition · what teachers read · status, who and the
  // History / Change values links. Cards below 660px.
  const columns = [
    {
      key: 'name',
      role: 'identifier',
      label: 'Rule',
      track: { compact: '38%', full: '36%' },
      render: (r) => <RuleCell r={r} />
    },
    {
      key: 'teacher',
      label: 'Teachers read',
      render: (r) =>
        r.teacher_title ? (
          <What title={r.teacher_title} lines={[r.teacher_why || null]} />
        ) : (
          <span className="tp-sub">Not shown to teachers</span>
        )
    },
    {
      key: 'status',
      role: 'status',
      label: 'Status',
      track: { compact: '248px', full: '264px' },
      render: (r) => (
        <StatusCell chip={version_chip(r)} meta={version_meta(r)} actions={columns_actions(r)} />
      ),
      card_render: (r) => <StatusCell chip={version_chip(r)} meta={version_meta(r)} />
    },
    {
      key: 'actions',
      label: 'Actions',
      role: 'actions',
      in_table: false,
      render: (r) => (
        <span className="tp-rx-actions">
          <button
            type="button"
            className="tp-link"
            aria-label={`History of ${r.name}`}
            onClick={() => setHistoryKey(r.rule_key)}
          >
            History
          </button>
          <button
            type="button"
            className="tp-link"
            disabled={!!edit_block || !can_change_version(r)}
            title={
              edit_block ||
              (!can_change_version(r) ? 'Only a rule in force can be changed here.' : undefined)
            }
            aria-label={`Change ${r.name}`}
            aria-describedby={edit_block ? 'rx-edit-block' : undefined}
            onClick={() => setEditing(r)}
          >
            Change values
          </button>
        </span>
      )
    }
  ];
  // A scheduled version is not changed here: History only.
  const history_only = (r) => (
    <span className="tp-rx-actions">
      <button
        type="button"
        className="tp-link"
        aria-label={`History of ${r.name}`}
        onClick={() => setHistoryKey(r.rule_key)}
      >
        History
      </button>
    </span>
  );
  const scheduled_columns = columns.map((c) => {
    if (c.key === 'status')
      return {
        ...c,
        render: (r) => (
          <StatusCell chip={version_chip(r)} meta={version_meta(r)} actions={history_only(r)} />
        )
      };
    if (c.key === 'actions') return { ...c, card_render: history_only };
    return c;
  });
  // The table puts the links under the status; a card puts them in its footer.
  function columns_actions(r) {
    return columns[columns.length - 1].render(r);
  }
  columns[columns.length - 1].card_render = columns_actions;

  return (
    <div className="tp-rx-tab">
      <p className="tp-rx-lead">
        How every class and month is paid. A change takes effect from a date and never touches a
        closed month.
      </p>
      <FilterBar
        label="Rule filters"
        summary={
          status === 'ready' && view.rule_count
            ? summary_line(
                rules_filter_summary({
                  count: shown_rule_count(stages),
                  filters,
                  at,
                  stage_label: stage_label(filters.stage)
                })
              )
            : null
        }
      >
        <div className="tp-fsearch">
          <input
            id="rx-q"
            className="tp-input"
            aria-label="Search rules"
            placeholder="Search name, wording or key"
            value={filters.q}
            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          />
        </div>
        <span className={`tp-fsel tp-rx-at ${at ? 'is-on' : ''}`.trim()}>
          <label htmlFor="rx-at" className="tp-fsel-k">
            In force on:
          </label>
          <input
            id="rx-at"
            type="date"
            className="tp-rx-at-in"
            aria-label="Rules in force on"
            value={at_draft}
            onChange={(e) => {
              setAtDraft(e.target.value);
              if (applicable_day(e.target.value)) setAt(e.target.value);
            }}
            aria-describedby={at ? 'rx-at-hint' : undefined}
          />
        </span>
        {/* Today needs no hint: the summary line says "in force today (IST)". */}
        {at ? (
          <span id="rx-at-hint" className="tp-rx-at-hint">
            <button type="button" className="tp-link" onClick={() => setAt('')}>
              Back to today
            </button>
          </span>
        ) : null}
        <SelectFilter
          id="rx-stage"
          label="Stage"
          aria_label="Filter by stage"
          value={filters.stage}
          all_label="All stages"
          options={catalogue.stages.map((st) => ({ value: st.code, label: st.label }))}
          onChange={(v) => setFilters((f) => ({ ...f, stage: v }))}
        />
        <Segmented
          label="On or off"
          options={RULE_STATUS_SEGMENTS}
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
        />
        {rules_filters_on(filters, at) ? (
          <button type="button" className="tp-link tp-fbar-end" onClick={clear_filters}>
            Clear filters
          </button>
        ) : null}
        {/* Every rule the filters leave on screen, across the stage and group cards. */}
        <span className={rules_filters_on(filters, at) ? undefined : 'tp-fbar-end'}>
          <DownloadCsvButton
            columns={RULE_CSV_COLUMNS}
            rows={rule_csv_rows(stages)}
            file_name={() => rules_csv_name('rules', at || view.today)}
            disabled={status !== 'ready'}
          />
        </span>
      </FilterBar>

      {status === 'loading' ? <CustomLoading /> : null}
      {status === 'error' ? <LoadFailed text={error} on_retry={reload} /> : null}
      {status === 'ready' && !view.rule_count ? (
        <div className="tp-empty">No rules were returned.</div>
      ) : null}
      {status === 'ready' && view.rule_count && !stages.length ? (
        <div className="tp-empty">No rule matches these filters.</div>
      ) : null}
      {status === 'ready' && edit_block ? (
        <div id="rx-edit-block" className="tp-banner tp-banner-warn" role="note">
          {edit_block}
        </div>
      ) : null}

      {status === 'ready'
        ? stages.map((s) => (
            <section key={s.stage} aria-labelledby={`tp-rx-stage-${s.stage}`}>
              <h3 className="tp-rx-stage" id={`tp-rx-stage-${s.stage}`}>
                {s.label}
              </h3>
              {s.groups.map((g) => (
                <div key={g.key} className="tp-card">
                  {/* A group named like its stage would repeat the heading: keep only its mode. */}
                  {g.name !== s.label || g.mode_text ? (
                    <div className="tp-card-hd">
                      {g.name !== s.label ? (
                        <span className="tp-card-hd-title">{g.name}</span>
                      ) : null}
                      {g.mode_text ? <span className="tp-card-hd-sub">{g.mode_text}</span> : null}
                    </div>
                  ) : null}
                  <DataTable
                    columns={columns}
                    rows={g.rules}
                    rowKey={(r) => r.key}
                    card_below={660}
                  />
                </div>
              ))}
            </section>
          ))
        : null}

      {status === 'ready' && view.scheduled.length ? (
        <section className="tp-card" aria-labelledby="tp-rx-scheduled">
          <div className="tp-card-hd">
            <span className="tp-card-hd-title" id="tp-rx-scheduled">
              Scheduled after {day_label(view.at)}
            </span>
            <span className="tp-card-hd-acts">
              <DownloadCsvButton
                columns={SCHEDULED_CSV_COLUMNS}
                rows={view.scheduled}
                file_name={() => rules_csv_name('scheduled', view.at)}
              />
            </span>
          </div>
          <DataTable
            columns={scheduled_columns}
            rows={view.scheduled}
            rowKey={(r) => r.key}
            card_below={660}
          />
        </section>
      ) : null}

      <ApprovalsQueue
        queue={queue}
        names={view.names}
        on_open={setOpenChange}
        on_retry={() => load_approvals()}
      />

      <ChangesLog
        log={log}
        names={view.names}
        on_open={setOpenChange}
        on_retry={() => load_changes(0)}
        on_more={() => load_changes(log.next_before)}
        more_loading={more_loading}
        more_error={more_error}
        load_all={changes_csv_loader(fetch_rule_changes)}
        on_error={(e) => {
          const r = refusal(e, 'Could not load the rule changes.');
          if (r.permission) on_error(e, r.text);
        }}
      />

      {history_key ? (
        <RuleHistoryDialog
          rule_key={history_key}
          on_close={() => setHistoryKey('')}
          on_error={on_error}
        />
      ) : null}
      {editing ? (
        <RuleEditDialog
          version={editing}
          list={view}
          names={view.names}
          catalogue={catalogue}
          now={now}
          poll_ms={poll_ms}
          new_request_id={new_request_id}
          prefill={prefill}
          on_close={() => {
            setEditing(null);
            setPrefill(null);
            changed();
          }}
          on_saved={reload}
          on_rules_changed={() => {
            setEditing(null);
            setPrefill(null);
            reload();
          }}
          on_error={on_error}
        />
      ) : null}
      {open_change ? (
        <TpDialog
          title={change_title(open_change)}
          busy={busy}
          size="xl"
          on_close={() => setOpenChange('')}
        >
          <div className="tp-modal tp-rx-cp">
            <ChangePanel
              change_uuid={open_change}
              names={view.names}
              catalogue={catalogue}
              now={now}
              poll_ms={poll_ms}
              on_saved={reload}
              on_changed={changed}
              on_rules_changed={() => {
                setOpenChange('');
                reload();
              }}
              on_busy={setBusy}
              on_error={on_error}
              can_prepare_again={(ch) => !!prepare_again_of(ch, view, catalogue)}
              on_prepare_again={(ch) => {
                const again = prepare_again_of(ch, view, catalogue);
                if (!again) return;
                setOpenChange('');
                setPrefill(again.prefill);
                setEditing(again.version);
              }}
            />
          </div>
        </TpDialog>
      ) : null}
    </div>
  );
};

export default RulesEngineTab;
